import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ActivityItem {
  type:
    | "module_completed"
    | "quiz_attempt"
    | "curriculum_enrolled"
    | "course_enrolled"
    | "certificate_earned"
    | "discussion_created"
    | "discussion_reply";
  description: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export function useLearnerActivity(learnerId: string | null) {
  return useQuery({
    queryKey: ["learner-activity", learnerId],
    queryFn: async (): Promise<ActivityItem[]> => {
      if (!learnerId) return [];

      const activities: ActivityItem[] = [];

      // Fetch all activity data in parallel
      const [
        moduleProgressResult,
        quizAttemptsResult,
        curriculumEnrollmentsResult,
        courseEnrollmentsResult,
        certificatesResult,
        courseDiscussionsResult,
        generalDiscussionsResult,
        discussionRepliesResult,
        generalRepliesResult,
      ] = await Promise.all([
        // Module completions
        supabase
          .from("module_progress")
          .select(`
            id,
            completed_at,
            modules:module_id (
              title,
              courses:course_id (
                title
              )
            ),
            course_enrollments:enrollment_id (
              user_id
            )
          `)
          .not("completed_at", "is", null)
          .order("completed_at", { ascending: false })
          .limit(50),
        // Quiz attempts
        supabase
          .from("quiz_attempts")
          .select(`
            id,
            completed_at,
            passed,
            percentage,
            quizzes:quiz_id (
              title,
              modules:module_id (
                title
              )
            )
          `)
          .eq("user_id", learnerId)
          .not("completed_at", "is", null)
          .order("completed_at", { ascending: false })
          .limit(20),
        // Curriculum enrollments
        supabase
          .from("curriculum_enrollments")
          .select(`
            id,
            enrolled_at,
            curricula:curriculum_id (
              title
            )
          `)
          .eq("user_id", learnerId)
          .order("enrolled_at", { ascending: false })
          .limit(10),
        // Course enrollments
        supabase
          .from("course_enrollments")
          .select(`
            id,
            enrolled_at,
            courses:course_id (
              title
            )
          `)
          .eq("user_id", learnerId)
          .order("enrolled_at", { ascending: false })
          .limit(10),
        // Certificates
        supabase
          .from("certificates")
          .select("id, issued_at, curriculum_title")
          .eq("user_id", learnerId)
          .order("issued_at", { ascending: false })
          .limit(10),
        // Course discussions
        supabase
          .from("course_discussions")
          .select(`
            id,
            created_at,
            title,
            courses:course_id (
              title
            )
          `)
          .eq("user_id", learnerId)
          .order("created_at", { ascending: false })
          .limit(10),
        // General discussions
        supabase
          .from("general_discussions")
          .select("id, created_at, title")
          .eq("user_id", learnerId)
          .order("created_at", { ascending: false })
          .limit(10),
        // Discussion replies
        supabase
          .from("discussion_replies")
          .select(`
            id,
            created_at,
            course_discussions:discussion_id (
              title
            )
          `)
          .eq("user_id", learnerId)
          .order("created_at", { ascending: false })
          .limit(10),
        // General discussion replies
        supabase
          .from("general_discussion_replies")
          .select(`
            id,
            created_at,
            general_discussions:discussion_id (
              title
            )
          `)
          .eq("user_id", learnerId)
          .order("created_at", { ascending: false })
          .limit(10),
      ]);

      // Process module completions - filter by user
      if (moduleProgressResult.data) {
        moduleProgressResult.data.forEach((mp) => {
          const enrollment = mp.course_enrollments as { user_id: string } | null;
          if (enrollment?.user_id !== learnerId) return;
          
          const module = mp.modules as { title: string; courses: { title: string } | null } | null;
          if (mp.completed_at && module) {
            activities.push({
              type: "module_completed",
              description: `Completed module "${module.title}"${module.courses ? ` in ${module.courses.title}` : ""}`,
              timestamp: mp.completed_at,
            });
          }
        });
      }

      // Process quiz attempts
      if (quizAttemptsResult.data) {
        quizAttemptsResult.data.forEach((qa) => {
          const quiz = qa.quizzes as { title: string; modules: { title: string } | null } | null;
          if (quiz && qa.completed_at) {
            activities.push({
              type: "quiz_attempt",
              description: `${qa.passed ? "Passed" : "Attempted"} quiz "${quiz.title}" with ${qa.percentage}%`,
              timestamp: qa.completed_at,
            });
          }
        });
      }

      // Process curriculum enrollments
      if (curriculumEnrollmentsResult.data) {
        curriculumEnrollmentsResult.data.forEach((ce) => {
          const curriculum = ce.curricula as { title: string } | null;
          if (curriculum) {
            activities.push({
              type: "curriculum_enrolled",
              description: `Enrolled in curriculum "${curriculum.title}"`,
              timestamp: ce.enrolled_at,
            });
          }
        });
      }

      // Process course enrollments
      if (courseEnrollmentsResult.data) {
        courseEnrollmentsResult.data.forEach((ce) => {
          const course = ce.courses as { title: string } | null;
          if (course) {
            activities.push({
              type: "course_enrolled",
              description: `Enrolled in course "${course.title}"`,
              timestamp: ce.enrolled_at,
            });
          }
        });
      }

      // Process certificates
      if (certificatesResult.data) {
        certificatesResult.data.forEach((c) => {
          activities.push({
            type: "certificate_earned",
            description: `Earned certificate for "${c.curriculum_title}"`,
            timestamp: c.issued_at,
          });
        });
      }

      // Process course discussions
      if (courseDiscussionsResult.data) {
        courseDiscussionsResult.data.forEach((d) => {
          const course = d.courses as { title: string } | null;
          activities.push({
            type: "discussion_created",
            description: `Started discussion "${d.title}"${course ? ` in ${course.title}` : ""}`,
            timestamp: d.created_at,
          });
        });
      }

      // Process general discussions
      if (generalDiscussionsResult.data) {
        generalDiscussionsResult.data.forEach((d) => {
          activities.push({
            type: "discussion_created",
            description: `Started general discussion "${d.title}"`,
            timestamp: d.created_at,
          });
        });
      }

      // Process discussion replies
      if (discussionRepliesResult.data) {
        discussionRepliesResult.data.forEach((r) => {
          const discussion = r.course_discussions as { title: string } | null;
          activities.push({
            type: "discussion_reply",
            description: `Replied to discussion "${discussion?.title || "Unknown"}"`,
            timestamp: r.created_at,
          });
        });
      }

      // Process general discussion replies
      if (generalRepliesResult.data) {
        generalRepliesResult.data.forEach((r) => {
          const discussion = r.general_discussions as { title: string } | null;
          activities.push({
            type: "discussion_reply",
            description: `Replied to discussion "${discussion?.title || "Unknown"}"`,
            timestamp: r.created_at,
          });
        });
      }

      // Sort by timestamp (newest first) and limit to 10
      return activities
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 10);
    },
    enabled: !!learnerId,
  });
}
